import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import { KeycloakService, fetchWithAutoRefresh } from 'auth/KeycloakService';
import * as Sentry from "@sentry/react";
import '../../styles/auth/signup.css';

import { 
  AlertCircle, 
  Mail, 
  Lock, 
  Loader2, 
  ArrowRight, 
  CheckCircle, 
  XCircle,
  Eye,
  EyeOff
} from 'lucide-react';
import { FcGoogle } from 'react-icons/fc';
// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'signup' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'signup' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'signup',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Responsive utilities
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      setIsTablet(width >= 640 && width < 1024);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return { isMobile, isTablet };
};

// Generic error messages for security
const GENERIC_ERROR_MESSAGES = {
  // Existing ones
  'NETWORK_ERROR': 'Network error. Please check your connection and try again.',
  'SERVER_ERROR': 'Service temporarily unavailable. Please try again later.',
  'AUTHENTICATION_FAILED': 'Authentication failed. Please try again.',
  'VALIDATION_ERROR': 'Please check your input and try again.',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',
  'EMAIL_ALREADY_REGISTERED': 'This email is already registered. Please sign in or use a different email address.',
  'REGISTRATION_FAILED': 'Registration failed. Please try again.',
  'EMAIL_SEND_FAILED': 'Unable to send verification email. Please try again later.',
  'INVALID_OTP': 'Invalid verification code. Please try again.',
  'SESSION_EXPIRED': 'Session expired. Please start over.',
    'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  // ✅ ADD THESE MISSING ONES:
  'ACCOUNT_CREATION_FAILED': 'Account creation failed. Please try again.',
  'ACCOUNT_SETUP_FAILED': 'Account setup failed. Please try again.',
  'REGISTRATION_ERROR': 'Registration failed. Please try again.',
  'DB_ERROR': 'Database error. Please try again later.',
  'INVALID_REQUEST': 'Invalid request. Please try again.',
  'INVALID_JSON': 'Invalid request format. Please try again.',
  'DATA_ERROR': 'Data processing error. Please try again.',
  'SERVICE_ERROR': 'Service error. Please try again later.',
  'PASSWORD_REQUIREMENTS_NOT_MET': 'Password does not meet security requirements.',
  'USER_CREATE_ERROR': 'Failed to create user account.',
  'OAUTH_INITIALIZATION_ERROR': 'Social login initialization failed.',
  'AUTHENTICATION_INTERRUPTED': 'Authentication was interrupted.',
  'GOOGLE_CONNECTION_ERROR': 'Could not connect to Google. Please try again.',
  'GOOGLE_SIGNIN_FAILED': 'Google sign-in failed. Please try again.',
  'GOOGLE_INFO_RETRIEVAL_FAILED': 'Failed to retrieve Google account information.',
  'MISSING_EMAIL': 'Email permission required for sign-up.',
  'CONFIG_ERROR': 'Service configuration error. Please contact support.',
  'AUTO_LOGIN_FAILED': 'Account created successfully, but auto-login failed. Please sign in manually.',
  'INVALID_RESPONSE': 'Invalid response received. Please try again.'
};

// Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Email validation for UX
const validateEmail = (email) => {
  if (!email || typeof email !== 'string') return { valid: false, error: 'Email is required' };
  const trimmedEmail = email.trim();
  if (trimmedEmail.length === 0) return { valid: false, error: 'Email is required' };
  if (trimmedEmail.length > 254) return { valid: false, error: 'Email too long' };
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmedEmail)) return { valid: false, error: 'Please enter a valid email address' };
  return { valid: true };
};

// Password validation for UX
const validatePassword = (password) => {
  if (!password || typeof password !== 'string') return { valid: false, error: 'Password is required' };
  if (password.length < 8) return { valid: false, error: 'Password must be at least 8 characters' };
  if (password.length > 128) return { valid: false, error: 'Password must be 128 characters or less' };
  if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
    return { valid: false, error: 'Password must contain uppercase, lowercase, and number' };
  }
  return { valid: true };
};

// OTP validation for UX
const validateOTP = (otp) => {
  if (!otp || typeof otp !== 'string') return { valid: false, error: 'Verification code is required' };
  const sanitizedOTP = otp.replace(/\D/g, '');
  if (sanitizedOTP.length !== 6) return { valid: false, error: 'Verification code must be 6 digits' };
  return { valid: true };
};

// Get safe error message
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return GENERIC_ERROR_MESSAGES['SERVER_ERROR'];
  }
   if (error.userMessage) {
    return error.userMessage;
  }
  
  if (error.error) {
    return error.error;
  }
  // Check backend error code first (in response data)
  if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
    return GENERIC_ERROR_MESSAGES[error.response.data.code];
  }
  
  // Check direct error code 
  if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
    return GENERIC_ERROR_MESSAGES[error.code];
  }
  
  // Handle HTTP status codes
  if (error.response?.status === 401 || error.status === 401) {
    return GENERIC_ERROR_MESSAGES['SESSION_EXPIRED'];
  }
  
  // Fallback to sanitized message if available
  if (error.message && typeof error.message === 'string') {
    return sanitizeText(error.message);
  }
  
  return GENERIC_ERROR_MESSAGES['SERVER_ERROR'];
};

const PROVIDERS = {
  EMAIL: 'email',
  GOOGLE: 'google'
};
const BACKEND_URL = 'https://barrack.ai';

const VerificationProgress = () => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsed(prev => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  let text = 'Verifying...';
  if (elapsed >= 10) text = 'Almost there...';
  else if (elapsed >= 5) text = 'Setting up your account...';

  return <span>{text}</span>;
};
const Signup = () => {
  const { isMobile, isTablet } = useResponsive();
  
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, handleEmailRegistration, handleSocialAuth } = useAuth();

  // Loading states
  const [loadingStates, setLoadingStates] = useState({
    emailSignup: false,
    socialSignup: false,
    otpVerification: false,
    resendOtp: false
  });

  const [formState, setFormState] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    verificationCode: ''
  });

  const [uiState, setUiState] = useState({
    currentProvider: null,
    showSuccess: false,
    passwordVisible: false,
    verificationSent: false,
    verificationEmail: '',
    resendDisabled: false,
    resendCountdown: 0
  });

  const [errorState, setErrorState] = useState({
    formErrors: {},
    authError: null,
    providerError: null,
    otpError: null
  });

  const [showEmailForm, setShowEmailForm] = useState(false);

  // Request cancellation
  const abortControllerRef = useRef(null);
  const signupTimeoutRef = useRef(null);
  const otpTimeoutRef = useRef(null);

  // Component mount logging
  useEffect(() => {
    logInfo('Signup component mounted', {
      isAuthenticated,
      isMobile,
      isTablet,
      currentPath: location.pathname
    });
  }, []);

  // Set Sentry context
  useEffect(() => {
    Sentry.setContext("signup", {
      isAuthenticated,
      currentProvider: uiState.currentProvider,
      verificationSent: uiState.verificationSent,
      showEmailForm,
      isMobile,
      isTablet,
      hasFormErrors: Object.keys(errorState.formErrors).length > 0,
      hasAuthError: !!errorState.authError
    });
  }, [isAuthenticated, uiState.currentProvider, uiState.verificationSent, 
      showEmailForm, isMobile, isTablet, errorState]);

  // Request cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (signupTimeoutRef.current) {
        clearTimeout(signupTimeoutRef.current);
      }
      if (otpTimeoutRef.current) {
        clearTimeout(otpTimeoutRef.current);
      }
      
      logInfo('Signup component unmounted', {
        hadPendingRequests: !!(abortControllerRef.current || signupTimeoutRef.current || otpTimeoutRef.current)
      });
    };
  }, []);

  // Form validation
  const formValidation = useMemo(() => {
    return {
      email: validateEmail(formState.email),
      password: validatePassword(formState.password),
      otp: validateOTP(formState.verificationCode)
    };
  }, [formState.email, formState.password, formState.verificationCode]);

  // Redirect if already authenticated
  useEffect(() => {
    if (location.pathname === '/signup' || location.pathname === '/login') return;
    if (isAuthenticated) {
      logUserAction('authenticated_user_redirect', {
        from: location.pathname,
        to: '/'
      });
      window.location.replace('/');
    }
  }, [isAuthenticated, location.pathname]);

  // Countdown timer for OTP expiration
  useEffect(() => {
    let timer;
    if (uiState.verificationSent && uiState.resendCountdown > 0) {
      timer = setInterval(() => {
        setUiState(prev => ({
          ...prev,
          resendCountdown: prev.resendCountdown - 1,
          resendDisabled: prev.resendCountdown > 1
        }));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [uiState.verificationSent, uiState.resendCountdown]);

  // Form validation function
  const validateForm = useCallback((values) => {
    const errors = {};
    let isValid = true;

    if (!formValidation.email.valid) {
      errors.email = formValidation.email.error;
      isValid = false;
    }

    if (!formValidation.password.valid) {
      errors.password = formValidation.password.error;
      isValid = false;
    }

    if (values.password !== values.confirmPassword) {
      errors.confirmPassword = "Passwords don't match";
      isValid = false;
    }

    logInfo('Form validation completed', {
      isValid,
      errorFields: Object.keys(errors),
      emailValid: formValidation.email.valid,
      passwordValid: formValidation.password.valid,
      passwordsMatch: values.password === values.confirmPassword
    });

    return { errors, isValid };
  }, [formValidation]);

  // Email signup handler
  const handleEmailSignup = useCallback(async (e) => {
    e.preventDefault();
    
    const startTime = performance.now();
    
    logUserAction('email_signup_initiated', {
      email: formState.email ? 'provided' : 'empty',
      passwordLength: formState.password.length
    });

    setErrorState({ formErrors: {}, authError: null, providerError: null });
    const validation = validateForm(formState);
    
    if (!validation.isValid) {
      setErrorState(prev => ({
        ...prev,
        formErrors: validation.errors
      }));
      
      logUserAction('email_signup_validation_failed', {
        validationErrors: Object.keys(validation.errors),
        email: formState.email ? 'provided' : 'empty'
      });
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setLoadingStates(prev => ({ ...prev, emailSignup: true }));
    setUiState(prev => ({ ...prev, currentProvider: PROVIDERS.EMAIL }));

    if (signupTimeoutRef.current) {
      clearTimeout(signupTimeoutRef.current);
    }

    signupTimeoutRef.current = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const safeMessage = getSafeErrorMessage({ code: 'NETWORK_ERROR' });
      setErrorState(prev => ({
        ...prev,
        authError: { code: 'NETWORK_ERROR', message: safeMessage }
      }));
      setLoadingStates(prev => ({ ...prev, emailSignup: false }));
      
      logError('Email signup timeout', {
        email: formState.email,
        timeoutDuration: 30000
      });
    }, 30000);

    try {
      logInfo('Email signup started', {
        email: formState.email,
        provider: PROVIDERS.EMAIL
      });
      
      const result = await handleEmailRegistration(
        sanitizeText(formState.email.trim()), 
        formState.password
      );
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      clearTimeout(signupTimeoutRef.current);
      
      if (!result.success) {
        const safeMessage = getSafeErrorMessage(result);
        
        setErrorState(prev => ({
          ...prev,
          authError: {
            code: result.code,
            message: safeMessage
          }
        }));
        
        setLoadingStates(prev => ({ ...prev, emailSignup: false }));
        
        logPerformance('email_signup', duration, {
          success: false,
          errorCode: result.code,
          email: formState.email
        });
        
        logError('Email signup failed', {
          errorCode: result.code,
          email: formState.email,
          duration: `${duration.toFixed(2)}ms`
        });
        
        logUserAction('email_signup_failed', {
          errorCode: result.code,
          duration: `${duration.toFixed(2)}ms`
        });
        return;
      }
      
      if (result.status === 'VERIFICATION_NEEDED') {
        setUiState(prev => ({
          ...prev,
          verificationSent: true,
          verificationEmail: sanitizeText(formState.email.trim()),
          resendCountdown: 60,
          resendDisabled: true,
          emailSignup: false
        }));
        
        logPerformance('email_signup', duration, {
          success: true,
          status: 'verification_needed',
          email: formState.email
        });
        
        logUserAction('verification_email_sent', {
          email: formState.email,
          duration: `${duration.toFixed(2)}ms`
        });
        
        logInfo('Email signup successful - verification needed', {
          email: formState.email,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        setUiState(prev => ({ 
          ...prev, 
          showSuccess: true,
          emailSignup: false
        }));
        
        logPerformance('email_signup', duration, {
          success: true,
          status: 'complete',
          email: result.email,
          provider: result.provider
        });
        
        logUserAction('email_signup_success', {
          email: result.email,
          provider: result.provider,
          duration: `${duration.toFixed(2)}ms`
        });
        
        logInfo('Email signup completed successfully', {
          email: result.email,
          provider: result.provider,
          duration: `${duration.toFixed(2)}ms`
        });
        
        setTimeout(() => navigate('/'), 1500);
      }
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      clearTimeout(signupTimeoutRef.current);
      if (error.name === 'AbortError') {
        logInfo('Email signup aborted by user');
        return;
      }
      
      const safeMessage = getSafeErrorMessage(error);
      
      setErrorState(prev => ({
        ...prev,
        authError: {
          code: error.code || 'REGISTRATION_FAILED',
          message: safeMessage
        }
      }));
      setLoadingStates(prev => ({ ...prev, emailSignup: false }));
      
      logPerformance('email_signup', duration, {
        success: false,
        error: safeMessage,
        errorName: error.name
      });
      
      logError('Email signup error', {
        errorCode: error.code,
        errorName: error.name,
        errorMessage: error.message,
        email: formState.email,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
      logUserAction('email_signup_error', {
        errorCode: error.code,
        errorName: error.name,
        duration: `${duration.toFixed(2)}ms`
      });
    }
  }, [formState, validateForm, handleEmailRegistration, navigate]);

  // OTP verification handler
  const handleVerifyOTP = useCallback(async (e) => {
    e.preventDefault();
    
    const startTime = performance.now();
    
    logUserAction('otp_verification_initiated', {
      email: uiState.verificationEmail,
      otpLength: formState.verificationCode.length
    });

    setErrorState({ 
      formErrors: {}, 
      authError: null, 
      providerError: null, 
      otpError: null 
    });
    
    if (!formValidation.otp.valid) {
      setErrorState(prev => ({
        ...prev,
        authError: { 
          code: 'VALIDATION_ERROR',
          message: formValidation.otp.error
        }
      }));
      
      logUserAction('otp_verification_validation_failed', {
        validationError: formValidation.otp.error,
        otpLength: formState.verificationCode.length
      });
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setLoadingStates(prev => ({ ...prev, otpVerification: true }));

    if (otpTimeoutRef.current) {
      clearTimeout(otpTimeoutRef.current);
    }

    otpTimeoutRef.current = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const safeMessage = getSafeErrorMessage({ code: 'NETWORK_ERROR' });
      setErrorState(prev => ({
        ...prev,
        authError: { code: 'NETWORK_ERROR', message: safeMessage }
      }));
      setLoadingStates(prev => ({ ...prev, otpVerification: false }));
      
      logError('OTP verification timeout', {
        email: uiState.verificationEmail,
        timeoutDuration: 45000
      });
    }, 45000);
    
    try {
      logInfo('OTP verification started', {
        email: uiState.verificationEmail,
        otpLength: formState.verificationCode.length
      });
      
      const response = await fetchWithAutoRefresh(`${BACKEND_URL}/api/auth/oauth/verify-registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
     body: JSON.stringify({
  email: sanitizeText(uiState.verificationEmail),
  code: sanitizeText(formState.verificationCode),
  password: formState.password
})
      });

      const endTime = performance.now();
      const duration = endTime - startTime;

      clearTimeout(otpTimeoutRef.current);

      let result;
      try {
        const text = await response.text();
        result = JSON.parse(text);
      } catch (parseError) {
        if (!response.ok) {
          result = {
            success: false,
            code: 'INVALID_OTP',
            message: 'Invalid verification code. Please try again.'
          };
        } else {
          throw new Error('Server returned invalid response format');
        }
      }
      
      if (result.success) {
        setUiState(prev => ({ ...prev, showSuccess: true }));
        
        logPerformance('otp_verification', duration, {
          success: true,
          email: result.email
        });
        
        logUserAction('otp_verification_success', {
          email: result.email,
          duration: `${duration.toFixed(2)}ms`
        });
        
        logInfo('OTP verification completed successfully', {
          email: result.email,
          duration: `${duration.toFixed(2)}ms`
        });
        
        setTimeout(() => navigate('/'), 1500);
      } else {
        const safeMessage = getSafeErrorMessage(result);
        
        setErrorState(prev => ({
          ...prev,
          authError: {
            code: result.code || 'VERIFICATION_FAILED',
            message: safeMessage
          }
        }));
        
        logPerformance('otp_verification', duration, {
          success: false,
          errorCode: result.code,
          email: uiState.verificationEmail
        });
        
        logError('OTP verification failed', {
          errorCode: result.code,
          email: uiState.verificationEmail,
          duration: `${duration.toFixed(2)}ms`
        });
        
        logUserAction('otp_verification_failed', {
          errorCode: result.code,
          duration: `${duration.toFixed(2)}ms`
        });
      }
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      clearTimeout(otpTimeoutRef.current);
      if (error.name === 'AbortError') {
        logInfo('OTP verification aborted by user');
        return;
      }
      
      const safeMessage = getSafeErrorMessage(error);
      
      setErrorState(prev => ({
        ...prev,
        authError: {
          code: error.code || 'SERVER_ERROR',
          message: safeMessage
        }
      }));
      
      logPerformance('otp_verification', duration, {
        success: false,
        error: safeMessage,
        errorName: error.name
      });
      
      logError('OTP verification error', {
        errorCode: error.code,
        errorName: error.name,
        errorMessage: error.message,
        email: uiState.verificationEmail,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
      logUserAction('otp_verification_error', {
        errorCode: error.code,
        errorName: error.name,
        duration: `${duration.toFixed(2)}ms`
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, otpVerification: false }));
    }
  }, [formState.verificationCode, uiState.verificationEmail, formValidation.otp, navigate]);

  // Social signup handler
  const handleSocialSignup = useCallback(async (provider) => {
    const startTime = performance.now();
    
    logUserAction('social_signup_initiated', {
      provider
    });
    
    setLoadingStates(prev => ({ ...prev, socialSignup: true }));
    setUiState(prev => ({ ...prev, currentProvider: provider }));
    
    try {
      logInfo('Social signup started', { provider });
      
      const response = await handleSocialAuth(provider, 'signup');
      
      if (response.authUrl) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        logPerformance('social_signup_init', duration, {
          success: true,
          provider,
          hasAuthUrl: !!response.authUrl
        });
        
        logUserAction('social_signup_redirect', {
          provider,
          duration: `${duration.toFixed(2)}ms`
        });
        
        logInfo('Social signup initialized successfully', {
          provider,
          duration: `${duration.toFixed(2)}ms`
        });

        window.location.href = response.authUrl;
      } else {
        throw new Error('No auth URL received');
      }
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      const safeMessage = getSafeErrorMessage(error);
      setErrorState(prev => ({
        ...prev,
        authError: {
          code: error.code || 'SOCIAL_SIGNUP_ERROR',
          message: safeMessage,
          details: error.details || null
        }
      }));
      setLoadingStates(prev => ({ ...prev, socialSignup: false }));
      
      logPerformance('social_signup_init', duration, {
        success: false,
        provider,
        error: safeMessage
      });
      
      logError('Social signup initialization failed', {
        provider,
        errorMessage: error.message,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
      logUserAction('social_signup_error', {
        provider,
        error: safeMessage,
        duration: `${duration.toFixed(2)}ms`
      });
      
      throw error;
    }
  }, [handleSocialAuth]);

  // Resend verification code handler
  const handleResendCode = useCallback(async () => {
    if (uiState.resendDisabled) return;
    
    const startTime = performance.now();
    
    logUserAction('resend_verification_initiated', {
      email: uiState.verificationEmail
    });
    
    setLoadingStates(prev => ({ 
      ...prev, 
      resendOtp: true,
      resendDisabled: true
    }));
    
    try {
      logInfo('Resend verification started', {
        email: uiState.verificationEmail
      });
      
      const result = await KeycloakService.handleEmailSignup(
        sanitizeText(uiState.verificationEmail), 
        formState.password
      );
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (result.success) {
        setUiState(prev => ({
          ...prev,
          resendCountdown: 60,
          resendDisabled: true
        }));
        
        setFormState(prev => ({
          ...prev,
          verificationCode: ''
        }));
        
        logPerformance('resend_verification', duration, {
          success: true,
          email: uiState.verificationEmail
        });
        
        logUserAction('resend_verification_success', {
          email: uiState.verificationEmail,
          duration: `${duration.toFixed(2)}ms`
        });
        
        logInfo('Verification email resent successfully', {
          email: uiState.verificationEmail,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        const safeMessage = getSafeErrorMessage(result);
        
        logPerformance('resend_verification', duration, {
          success: false,
          error: safeMessage,
          email: uiState.verificationEmail
        });
        
        logError('Resend verification failed', {
          email: uiState.verificationEmail,
          error: safeMessage,
          duration: `${duration.toFixed(2)}ms`
        });
        
        logUserAction('resend_verification_failed', {
          error: safeMessage,
          duration: `${duration.toFixed(2)}ms`
        });
      }
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      const safeMessage = getSafeErrorMessage(error);
      
      logPerformance('resend_verification', duration, {
        success: false,
        error: safeMessage,
        errorName: error.name
      });
      
      logError('Resend verification error', {
        email: uiState.verificationEmail,
        errorName: error.name,
        errorMessage: error.message,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
      logUserAction('resend_verification_error', {
        errorName: error.name,
        duration: `${duration.toFixed(2)}ms`
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, resendOtp: false }));
    }
  }, [uiState.verificationEmail, uiState.resendDisabled, formState.password]);

  // Input handlers
  const handleInputChange = useCallback((field, value) => {
    setFormState(prev => ({ ...prev, [field]: value }));
    setErrorState(prev => ({
      ...prev,
      authError: null,
      providerError: null,
      formErrors: { ...prev.formErrors, [field]: null }
    }));
    
    logUserAction('form_field_changed', {
      field,
      hasValue: !!value,
      valueLength: typeof value === 'string' ? value.length : 0
    });
  }, []);

  const handleShowEmailForm = useCallback(() => {
    setShowEmailForm(true);
    
    logUserAction('email_form_shown', {
      previousState: 'provider_selection'
    });
    
    logInfo('Email signup form displayed', {
      isMobile,
      isTablet
    });
  }, [isMobile, isTablet]);

  const handleBackToProviders = useCallback(() => {
    setShowEmailForm(false);
    
    // Clear form data when going back
    setFormState({
      email: '',
      password: '',
      confirmPassword: '',
      verificationCode: ''
    });
    
    // Clear any errors
    setErrorState({
      formErrors: {},
      authError: null,
      providerError: null,
      otpError: null
    });
    
    logUserAction('back_to_providers', {
      fromEmailForm: true
    });
    
    logInfo('Returned to provider selection', {
      hadFormData: !!(formState.email || formState.password)
    });
  }, [formState.email, formState.password]);

  // Render error block
  const renderError = useCallback(() => {
    if (errorState.authError) {
      return (
        <div className="auth-error-container">
          <div className="auth-error-header">
            <AlertCircle size={18} />
            <span>Error</span>
          </div>
          <p>{sanitizeText(errorState.authError.message)}</p>
        </div>
      );
    }
    return null;
  }, [errorState.authError]);


  // Render verification screen
  const renderVerificationScreen = () => (
    <div className="auth-signup-container slide-up">
      <div className="auth-header">
        <h2 className="auth-title">Verify Your Email</h2>
        <p className="auth-subtitle">
          We sent a verification code to {sanitizeText(uiState.verificationEmail)}
        </p>
      </div>

      {errorState.otpError && (
        <div className="auth-error-container">
          <div className="auth-error-header">
            <AlertCircle size={18} />
            <span>Verification Failed</span>
          </div>
          <p>{sanitizeText(errorState.otpError.message)}</p>
        </div>
      )}

      {renderError()}

      <form onSubmit={handleVerifyOTP} className="auth-form">
        <div className="form-group">
          <label htmlFor="verificationCode">Verification Code</label>
          <input
            id="verificationCode"
            type="text"
            required
            className="input-field"
            placeholder="Enter 6-digit code"
            value={formState.verificationCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              handleInputChange('verificationCode', value);
            }}
            maxLength={6}
          />
          <div className="expiry-notice">
            This code will expire in 10 minutes
          </div>
        </div>

        <div className="resend-container">
          <button
            type="button"
            onClick={handleResendCode}
            disabled={uiState.resendDisabled || loadingStates.resendOtp}
            className={`resend-button ${uiState.resendDisabled ? 'disabled' : ''}`}
          >
            {loadingStates.resendOtp ? 'Sending...' :
             uiState.resendDisabled 
              ? `Resend code (${uiState.resendCountdown}s)` 
              : 'Resend code'}
          </button>
        </div>

        <button 
          type="submit" 
          disabled={loadingStates.otpVerification} 
          className="submit-button"
        >
          {loadingStates.otpVerification ? (
            <>
              <Loader2 size={20} className="spinner" />
              <VerificationProgress />
            </>
          ) : (
            <>
              <span>Verify Account</span>
              <ArrowRight size={18} />
            </>
          )}
        </button>
      </form>

      <div className="auth-footer">
        <button onClick={() => navigate('/login')} className="auth-link">
          Back to login
        </button>
      </div>
    </div>
  );
  
  return (
    <div className={`auth-container centered ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
      <div className="auth-main">
        {uiState.verificationSent ? (
          renderVerificationScreen()
        ) : (
          <div className={`auth-signup-container ${showEmailForm ? 'email-form fade-in' : ''}`}>
            <div className="auth-header">
              <h1 className="auth-title">Create your account</h1>
              <p className="auth-subtitle">Get started with Barrack today</p>
            </div>

            {renderError()}

            {!showEmailForm ? (
              <>
                <div className="social-buttons">
               <button
  onClick={() => handleSocialSignup(PROVIDERS.GOOGLE)}
  disabled={loadingStates.socialSignup}
  className="social-button"
>
  {loadingStates.socialSignup && uiState.currentProvider === PROVIDERS.GOOGLE ? (
    <Loader2 size={20} className="spinner" />
  ) : (
    <FcGoogle size={20} />
  )}
  <span>Continue with Google</span>
</button>
                </div>

                <div className="divider">
                  <span>or</span>
                </div>

                <button onClick={handleShowEmailForm} className="auth-text-button">
                  Sign up with email
                </button>

                <div className="auth-footer">
                  Already have an account?
                  <button type="button" onClick={() => {
                    logUserAction('signin_link_clicked', { from: 'signup_page' });
                    navigate('/login');
                  }} className="auth-link">
                    Sign in
                  </button>
                </div>
              </>
            ) : (
              <>
                <form onSubmit={handleEmailSignup} className="auth-form">
                  <div className="form-group">
                    <label htmlFor="email">Email address</label>
                    <input
                      id="email"
                      type="email"
                      required
                      autoComplete="email"
                      className={`input-field ${errorState.formErrors.email ? 'error' : ''}`}
                      placeholder="name@company.com"
                      value={formState.email}
                      onChange={(e) => handleInputChange('email', e.target.value.trim())}
                      maxLength={254}
                    />
                    {errorState.formErrors.email && (
                      <span className="field-error">{sanitizeText(errorState.formErrors.email)}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <div className="password-field">
                      <input
                        id="password"
                        type={uiState.passwordVisible ? 'text' : 'password'}
                        required
                        autoComplete="new-password"
                        className={`input-field ${errorState.formErrors.password ? 'error' : ''}`}
                        placeholder="Create a strong password"
                        value={formState.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        maxLength={128}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => {
                          const newVisible = !uiState.passwordVisible;
                          setUiState(prev => ({
                            ...prev,
                            passwordVisible: newVisible
                          }));
                          
                          logUserAction('password_visibility_toggled', {
                            visible: newVisible,
                            field: 'password'
                          });
                        }}
                        aria-label={uiState.passwordVisible ? "Hide password" : "Show password"}
                      >
                        {uiState.passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {errorState.formErrors.password && (
                      <span className="field-error">{sanitizeText(errorState.formErrors.password)}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm password</label>
                    <input
                      id="confirmPassword"
                      type={uiState.passwordVisible ? 'text' : 'password'}
                      required
                      className={`input-field ${errorState.formErrors.confirmPassword ? 'error' : ''}`}
                      placeholder="Confirm your password"
                      value={formState.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      maxLength={128}
                    />
                    {errorState.formErrors.confirmPassword && (
                      <span className="field-error">{errorState.formErrors.confirmPassword}</span>
                    )}
                  </div>

                  <button type="submit" disabled={loadingStates.emailSignup} className="submit-button">
                    {loadingStates.emailSignup && uiState.currentProvider === PROVIDERS.EMAIL ? (
                      <>
                        <Loader2 size={20} className="spinner" />
                        <span>Creating account...</span>
                      </>
                    ) : (
                      <>
                        <span>Create Account</span>
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </form>

                <button type="button" onClick={handleBackToProviders} className="auth-link back-link">
                  ← Back to signup options
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
  
export default Signup;